// cleanup_console_errors_eslint_SECURITY_CONTACT

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Dropdown } from '@glu/dropdown-react';

import DefaultButton from 'components/defaultButton/DefaultButton';
import useLocale from 'components/useLocale';
import { SecurityContact } from 'proptypes/user';

import SecurityActivation from './SecurityActivation';
import SecurityContactField from './SecurityContactField';
import { contactTypes, VRU_DELAY_OPTIONS } from './securityContacts.constants';
import useStyles from './SecurityContact.styles';

const SecurityContactComponent = ({
  contact,
  disabled,
  handleError: handleErrorProp,
  onChange,
  onReset,
  resetError
}) => {
  const { defaultButton, extensionLabel, extensionOpt, extensionTitle } = useStyles();
  const locale = useLocale();
  const {
    contactType,
    default: defaulted,
    errorMessage,
    extension = '',
    helpText,
    label,
    name,
    sent,
    value,
    verified
  } = contact;

  const showActivation =
    contactType === contactTypes.sms && !verified && sent && value.length >= 10;
  const verifiedSMS = verified || contactType !== contactTypes.sms;
  const showDefault = verifiedSMS && value && !errorMessage;

  const showExt = !verified && contactType === contactTypes.voice;
  const isVoice = contactType === contactTypes.voice;

  const change = (changeValue) => {
    if (!changeValue.target) {
      return onChange({
        ...contact,
        ...changeValue,
        extension
      });
    }

    const { name, value } = changeValue.target;

    if (name === 'extension') {
      return onChange({ ...contact, extension: value.replace(/\D/g, '') });
    }

    if (name === 'vruDelay') {
      return onChange({ ...contact, vruDelay: value });
    }

    return onChange({ ...contact, ...{ [name]: value } });
  };

  const setDefault = () => {
    change({ default: true });
  };

  const reset = () => {
    onReset(contact);
  };

  const handleError = (data) => {
    const code = data?.[0]?.code;
    const userMessage = data?.[0]?.userMessage;
    change({
      errorMessage: locale[code] || userMessage || locale['common.server.error'],
      successMessage: ''
    });
  };

  return (
    <article className="row no-gutters">
      <div
        className={classnames('form-group', {
          'col-2': isVoice && showExt,
          'col-6': !isVoice || !showExt,
          'has-error': errorMessage
        })}
      >
        <label className="control-label" htmlFor={name} data-qa={`${contact.contactType}-label`}>
          {label}
        </label>
        <SecurityContactField
          disabled={disabled}
          contact={contact}
          onChange={change}
          onReset={onReset}
          handleError={handleErrorProp}
          resetError={resetError}
        />
        {errorMessage && (
          <div>
            <span className="help-block">{errorMessage}</span>
          </div>
        )}
        {!verified && value && (
          <small data-qa={`${contact.contactType}-help-text`}>{helpText}</small>
        )}
      </div>
      {showExt && (
        <div className="col-2">
          <label htmlFor="extension" className={extensionLabel}>
            <div className={extensionTitle}>{locale['profile.label.ext']}</div>
            <div className={extensionOpt}>{locale['common.optional']}</div>
          </label>
          <input
            className="form-control"
            disabled={disabled}
            onChange={change}
            id="extension"
            name="extension"
            type="text"
            maxLength="10"
            value={value && extension}
          />
        </div>
      )}
      {showExt && (
        <div className="col-2">
          <Dropdown
            name="vruDelay"
            dataQa="vruDelay"
            data-qa="vruDelay-dropdown"
            labelText={locale['profile.label.extDialDelay']}
            filterEnabled={false}
            options={VRU_DELAY_OPTIONS}
            optional={false}
            disabled={!extension || !value}
            onChange={(name, value) => {
              change({ target: { name, value: value[0].id } });
            }}
            preSelectFirstOption
          />
        </div>
      )}

      {showDefault && (
        <div className={classnames(defaultButton, 'col-1')}>
          <DefaultButton
            default={defaulted}
            disabled={disabled}
            name={name}
            onChange={setDefault}
          />
        </div>
      )}

      {showActivation && (
        <div className="col-xs-6 col-sm-4">
          <SecurityActivation
            contact={contact}
            disabled={disabled}
            name={name}
            onError={handleError}
            onReset={reset}
            onVerified={change}
          />
        </div>
      )}
    </article>
  );
};

export default SecurityContactComponent;

SecurityContactComponent.defaultProps = {
  contact: {},
  disabled: false,
  onChange() {},
  onReset() {}
};

SecurityContactComponent.propTypes = {
  contact: SecurityContact,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  handleError: PropTypes.func.isRequired,
  resetError: PropTypes.func.isRequired
};
